import React, { useMemo, useState } from "react";
import {
  ArrowDown, Check, ChevronRight, CircleAlert, GitCompareArrows, MessagesSquare, Rows3, Waypoints, X,
} from "lucide-react";
import { StatusPill } from "./traces/atoms";
import { runDuration, fmtMs, fmtCost, fmtTokens } from "@/shared/lib/runs";
import {
  SOURCE_META, traceId, traceCost, traceAnalytics,
  sessionsOf, errorObservations,
} from "./traceModel";
import { CopyId } from "./TraceDetail";
import RecordedTrace from "./RecordedTrace";
import { useRecordedRuns, useSpanTrees } from "./useRecorded";
import { catalogFor, interfaceName } from "@/shared/lib/interface-catalog";
import { ViaIcon } from "@/shared/components/ViaIcon";
import { AgentPage, BrandButton, CloseButton, GhostButton, SearchInput, Sparkline } from "@/shared/components/ui-kit";
import { blockedMeta } from "./traces/status";
import { ActiveFilters, channelOf, NO_FILTERS, passesFilters, RunFilterMenu } from "./traces/RunFilters";
import { Segmented } from "@/shared/components/teams-ui";

import type { Agent, NavigationOptions, Run } from "@/shared/types";
import type { Span } from "./types";
import { StatTile } from "@/shared/components/StatTile";
import type { RunFilterState } from "./traces/RunFilters";

/** A run's span tree, whichever kind of run it is (`useSpanTrees`). */
type TreeOf = (run: Run) => Span;

/** Open a run's trace, optionally landing on one span inside it. */
type OpenRun = (runId: string, spanId?: string | null) => void;

/* ------------------------------- stats row -------------------------------- */

// "Today"/"Yesterday"/"2 days ago"/"a week ago" → days back; >7 falls off the chart.
function daysAgo(when: string | undefined): number {
  const w = (when || "").toLowerCase();
  if (w.includes("today") || w === "now") return 0;
  if (w.includes("yesterday")) return 1;
  const d = w.match(/(\d+)\s*d/);
  if (d) return +d[1];
  if (w.includes("week")) return 7;
  const wk = w.match(/(\d+)\s*w/);
  if (wk) return +wk[1] * 7;
  return 30;
}

function StatsRow({ agentRuns }: { agentRuns: Run[] }) {
  const stats = useMemo(() => {
    const cost = agentRuns.reduce((n, r) => n + traceCost(r), 0);
    const avg = agentRuns.length ? agentRuns.reduce((n, r) => n + runDuration(r), 0) / agentRuns.length : 0;
    const errors = agentRuns.filter((r) => r.status === "error").length;
    const counts = Array.from({ length: 8 }, (_, i) => agentRuns.filter((r) => daysAgo(r.when) === 7 - i).length);
    return { cost, avg, errors, counts };
  }, [agentRuns]);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-1 p-1 rounded-[18px] bg-[var(--surface-2)] mb-5">
      <StatTile label="Spend · 7d" value={fmtCost(stats.cost)} sub="cache discounts applied" />
      <StatTile label="Runs · 7d" value={agentRuns.length} sub="per day →">
        <Sparkline data={stats.counts} width={80} height={24} dot className="text-[var(--text-quaternary)]" />
      </StatTile>
      <StatTile label="Avg duration" value={fmtMs(Math.round(stats.avg))} sub="wall-clock per run" />
      <StatTile
        label="Error rate"
        value={`${agentRuns.length ? Math.round((stats.errors / agentRuns.length) * 100) : 0}%`}
        sub={`${stats.errors} failed run${stats.errors === 1 ? "" : "s"}`}
      />
    </div>
  );
}

/* -------------------------------- run rows -------------------------------- */

/** Picking runs to compare: whether the row can be picked, is, and how to pick it. */
interface Picking {
  picked: string[];
  /** Every row shows its box, not only the hovered one. */
  choosing: boolean;
  onPick: (id: string) => void;
}

function RunRow({ run, onOpen, picking }: { run: Run; onOpen: OpenRun; picking?: Picking }) {
  const src = SOURCE_META[run.source];
  const channel = run.recorded?.interface;
  const via = channel ? interfaceName(channel) : src.label;
  const blocked = blockedMeta(run.blockedReason);
  const picked = !!picking?.picked.includes(run.id);
  // once one run is picked, or Compare two runs is on, every row shows its box
  const boxes = !!picking && (picking.choosing || picking.picked.length > 0);
  return (
    <div className={`group relative ${picked ? "bg-[var(--accent-active)]" : ""}`}>
      <button
        onClick={() => onOpen(run.id)}
        className={`press w-full flex items-center gap-3 sm:gap-4 px-3 py-3 text-left transition-colors ${picked ? "" : "group-hover:bg-[var(--accent-hover)]"}`}
      >
        {/* The icon says where the run came from, as in the chat list: a channel's own glyph, else
            chat or task. The box that picks the run for comparing takes its place: on a desktop's
            hover, or on every row once comparing (the touch layout below lg has no hover). */}
        <span className="shrink-0 w-4 h-4 flex items-center justify-center">
          <span
            role="img"
            aria-label={via}
            title={via}
            className={`flex text-[var(--text-tertiary)] transition-opacity ${picking && (boxes || picked) ? "opacity-0" : picking ? "lg:group-hover:opacity-0" : ""}`}
          >
            {channel && catalogFor(channel) ? (
              <ViaIcon type={channel} decorative className="!text-current" />
            ) : (
              <src.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
            )}
          </span>
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13.5px] font-medium text-[var(--text-primary)] tracking-[-0.005em] truncate">
            {run.title}
          </span>
          {/* A run that has stopped says why on the row. "Needs attention" with no
              reason forces everyone to open the trace to find out whether it is
              their problem. */}
          {/* On a phone the status and duration ride under the title instead of taking its width */}
          <span className="sm:hidden flex items-center gap-2 mt-1">
            <StatusPill status={run.status} />
            <span className="font-mono text-[12px] tabular-nums text-[var(--text-secondary)]">{fmtMs(runDuration(run))}</span>
          </span>
          {blocked ? (
            <span className="block mt-[2px] text-[11.5px] text-[var(--warn-text)] truncate">{blocked.label}</span>
          ) : (
            <span className="flex items-center gap-1.5 mt-[2px] min-w-0">
              <CopyId id={traceId(run)} />
            </span>
          )}
        </span>
        <span className="hidden sm:block md:w-[118px] shrink-0"><StatusPill status={run.status} /></span>
        <span className="hidden sm:block w-[56px] shrink-0 font-mono text-[12px] tabular-nums text-[var(--text-primary)] text-right">
          {fmtMs(runDuration(run))}
        </span>
        <span className="hidden sm:block w-[56px] shrink-0 font-mono text-[12px] tabular-nums text-[var(--text-secondary)] text-right">
          {fmtCost(traceCost(run))}
        </span>
        <span className="hidden md:block w-[104px] shrink-0 text-right">
          <span className="block text-[12px] text-[var(--text-secondary)]">{run.when}</span>
          <span className="block text-[11px] font-mono text-[var(--text-quaternary)] mt-[1px]">{run.timeLabel}</span>
        </span>
        <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
      </button>
      {/* beside the row's button, not in it: one control never holds another */}
      {picking && (
        <button
          type="button"
          role="checkbox"
          aria-checked={picked}
          aria-label={`Pick ${run.title} to compare`}
          // a hover box is the mouse's shortcut; the keyboard's way in is Compare two runs
          tabIndex={boxes ? 0 : -1}
          aria-hidden={boxes ? undefined : true}
          onClick={() => picking.onPick(run.id)}
          className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-[5px] items-center justify-center transition-opacity ${
            picked ? "bg-[var(--brand)]" : "bg-[var(--surface)] ring-1 ring-[var(--hairline-focus)]"
          } ${boxes ? "flex opacity-100" : "hidden lg:flex opacity-0 lg:group-hover:opacity-100"}`}
        >
          {picked && <Check className="w-2.5 h-2.5 text-[var(--brand-foreground)]" strokeWidth={3} />}
        </button>
      )}
    </div>
  );
}

/** Newest or oldest first: the Started column's header flips it. */
interface Sorting {
  newestFirst: boolean;
  onFlip: () => void;
}

function ListHeader({ sort }: { sort?: Sorting }) {
  return (
    <div className="flex items-center gap-3 sm:gap-4 px-3 pb-2">
      <span className="w-4 shrink-0" />
      <span className="flex-1 section-label">Run</span>
      <span className="hidden sm:block md:w-[118px] shrink-0 section-label">Status</span>
      <span className="hidden sm:block w-[56px] shrink-0 section-label text-right">Duration</span>
      <span className="hidden sm:block w-[56px] shrink-0 section-label text-right">Cost</span>
      <span className="hidden md:flex w-[104px] shrink-0 justify-end">
        {sort ? (
          <button
            type="button"
            onClick={sort.onFlip}
            title={sort.newestFirst ? "Newest first. Click for oldest first." : "Oldest first. Click for newest first."}
            aria-label={sort.newestFirst ? "Started, newest first" : "Started, oldest first"}
            className="press -my-1 -mr-1.5 inline-flex items-center gap-1 h-6 px-1.5 rounded-[7px] section-label hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
          >
            Started
            <ArrowDown className={`w-3 h-3 transition-transform [transition-duration:300ms] [transition-timing-function:var(--easing)] ${sort.newestFirst ? "" : "rotate-180"}`} strokeWidth={1.75} />
          </button>
        ) : (
          <span className="section-label">Started</span>
        )}
      </span>
      <span className="w-3.5 shrink-0" />
    </div>
  );
}

function RunTable({ runs, onOpen, picking, sort }: {
  runs: Run[];
  onOpen: OpenRun;
  picking?: Picking;
  sort?: Sorting;
}) {
  return (
    <div className="rounded-[18px] bg-[var(--surface-2)] p-1 pt-2.5">
      <ListHeader sort={sort} />
      <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift overflow-hidden divide-y divide-[var(--hairline)]">
        {runs.map((r) => <RunRow key={r.id} run={r} onOpen={onOpen} picking={picking} />)}
      </div>
    </div>
  );
}

/* ------------------------------ compare view ------------------------------ */

function compareStats(run: Run, root: Span) {
  const a = traceAnalytics(root);
  return {
    run,
    duration: root.dur,
    cost: a.cost,
    tokensIn: a.tokens.input,
    tokensOut: a.tokens.output,
    cached: a.tokens.cached,
    gens: a.llmCount,
    tools: a.toolCount,
    errors: a.errors,
  };
}

// Delta chip for run B vs run A — green when lower, red when higher.
function Delta({ a, b, lowerIsBetter = true }: { a: number; b: number; lowerIsBetter?: boolean }) {
  if (!a || a === b) return null;
  const pct = Math.round(((b - a) / a) * 100);
  const better = lowerIsBetter ? pct < 0 : pct > 0;
  return (
    <span className={`ml-1.5 font-mono text-[10.5px] font-medium ${better ? "text-[var(--success)]" : "text-[var(--danger)]"}`}>
      {pct > 0 ? "+" : ""}{pct}%
    </span>
  );
}

function CompareView({ runs, treeOf, onClose, onOpen }: {
  runs: Run[];
  treeOf: TreeOf;
  onClose: () => void;
  onOpen: OpenRun;
}) {
  const [A, B] = runs.map((r) => compareStats(r, treeOf(r)));
  const rows = [
    ["Duration", fmtMs(A.duration), fmtMs(B.duration), <Delta a={A.duration} b={B.duration} />],
    ["Cost", fmtCost(A.cost), fmtCost(B.cost), <Delta a={A.cost} b={B.cost} />],
    ["Tokens in", fmtTokens(A.tokensIn), fmtTokens(B.tokensIn), <Delta a={A.tokensIn} b={B.tokensIn} />],
    ["Tokens out", fmtTokens(A.tokensOut), fmtTokens(B.tokensOut), <Delta a={A.tokensOut} b={B.tokensOut} />],
    ["Cached reads", fmtTokens(A.cached), fmtTokens(B.cached), <Delta a={A.cached} b={B.cached} lowerIsBetter={false} />],
    ["Generations", A.gens, B.gens, <Delta a={A.gens} b={B.gens} />],
    ["Tool calls", A.tools, B.tools, <Delta a={A.tools} b={B.tools} />],
    ["Failed steps", A.errors, B.errors, <Delta a={A.errors} b={B.errors} />],
  ] as const;
  return (
    <div className="fade-in-up">
      <div className="flex items-center gap-2 mb-4">
        <GhostButton onClick={onClose}>
          <X className="w-3.5 h-3.5" strokeWidth={1.75} /> Close comparison
        </GhostButton>
      </div>
      <div className="tray-box overflow-hidden">
        <div className="grid grid-cols-[140px_1fr_1fr] border-b hairline">
          <span />
          {[A, B].map(({ run }) => (
            <div key={run.id} className="px-4 py-3 min-w-0 border-l hairline">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[13.5px] font-medium text-[var(--text-primary)] truncate">{run.title}</span>
                <StatusPill status={run.status} />
              </div>
              <div className="flex items-center gap-2.5 mt-1">
                <CopyId id={traceId(run)} />
                <button
                  onClick={() => onOpen(run.id)}
                  className="press text-[11.5px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] underline underline-offset-2 decoration-[var(--hairline-focus)]"
                >
                  Open trace
                </button>
              </div>
            </div>
          ))}
        </div>
        {rows.map(([label, va, vb, delta]) => (
          <div key={label} className="grid grid-cols-[140px_1fr_1fr] border-b hairline last:border-b-0">
            <span className="px-4 py-2.5 text-[11.5px] text-[var(--text-tertiary)]">{label}</span>
            <span className="px-4 py-2.5 font-mono text-[12.5px] tabular-nums text-[var(--text-primary)] border-l hairline">{va}</span>
            <span className="px-4 py-2.5 font-mono text-[12.5px] tabular-nums text-[var(--text-primary)] border-l hairline">
              {vb}
              {delta}
            </span>
          </div>
        ))}
      </div>
      <p className="text-[11.5px] text-[var(--text-quaternary)] mt-3 m-0">
        Deltas are the right run relative to the left. Green is better — lower spend and latency, more cache reads.
      </p>
    </div>
  );
}

/* ------------------------------ sessions view ----------------------------- */

function SessionsView({ agentRuns, onOpen }: { agentRuns: Run[]; onOpen: OpenRun }) {
  const sessions = useMemo(() => sessionsOf(agentRuns), [agentRuns]);
  if (sessions.length === 0)
    return <div className="py-12 text-center text-[13px] text-[var(--text-tertiary)] tray-box">No sessions yet.</div>;
  return (
    <div className="space-y-5">
      {sessions.map((s) => {
        const Icon = SOURCE_META[s.source].Icon;
        const cost = s.runs.reduce((n, r) => n + traceCost(r), 0);
        return (
          <section key={s.key}>
            <div className="flex items-center gap-2 mb-2 min-w-0">
              <Icon className="w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
              <span className="text-[13.5px] font-medium text-[var(--text-primary)] truncate">{s.title}</span>
              <span className="section-label">
                {s.runs.length} run{s.runs.length === 1 ? "" : "s"} · {fmtCost(cost)} · last {s.runs[0].when.toLowerCase()}
              </span>
            </div>
            <RunTable runs={s.runs} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

/* ------------------------------- errors view ------------------------------ */

function ErrorsView({ agent, agentRuns, treeOf, onOpen }: { agent: Agent; agentRuns: Run[]; treeOf: TreeOf; onOpen: OpenRun }) {
  const errors = useMemo(() => errorObservations(agentRuns, treeOf), [agentRuns, treeOf]);
  if (errors.length === 0)
    return (
      <div className="py-14 text-center tray-box">
        <Check className="w-5 h-5 mx-auto mb-2 text-[var(--success)]" strokeWidth={1.5} />
        <div className="text-[14px] font-medium text-[var(--text-primary)]">No failed steps</div>
        <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1">Every observation across {agent.name}'s runs completed cleanly.</div>
      </div>
    );
  const groups = [...new Set(errors.map((e) => (e.span.raw?.tool || e.span.name).split(".")[0]))].map((fam) => ({
    fam,
    items: errors.filter((e) => (e.span.raw?.tool || e.span.name).split(".")[0] === fam),
  }));
  return (
    <div className="space-y-5">
      {groups.map((g) => (
        <section key={g.fam}>
          <div className="section-label mb-2">{g.fam} · {g.items.length}</div>
          <div className="tray-box overflow-hidden divide-y divide-[var(--hairline)]">
            {g.items.map(({ run, span }) => (
              <button
                key={`${run.id}-${span.id}`}
                onClick={() => onOpen(run.id, span.id)}
                className="press w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-[var(--accent-hover)] transition-colors"
              >
                <span className="shrink-0 w-6 h-6 rounded-[7px] bg-[var(--danger-bg)] ring-1 ring-[var(--danger-ring)] flex items-center justify-center">
                  <CircleAlert className="w-3 h-3 text-[var(--danger)]" strokeWidth={1.75} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[12.5px] font-medium text-[var(--text-primary)]">{span.name}</span>
                    <span className="text-[12px] text-[var(--text-tertiary)] truncate">{span.label}</span>
                  </span>
                  <span className="block text-[11.5px] text-[var(--danger)] truncate mt-[2px]">
                    {span.io.output?.error || "Step failed"}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-[12px] text-[var(--text-secondary)] truncate max-w-[160px]">{run.title}</span>
                  <span className="block text-[11px] font-mono text-[var(--text-quaternary)] mt-[1px]">{run.when} · {fmtMs(span.dur)}</span>
                </span>
                <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* -------------------------------- run list -------------------------------- */

/** Which list the traces screen is showing. */
type Mode = "runs" | "sessions" | "errors";

const modes = (errorCount: number): { value: Mode; label: React.ReactNode }[] => [
  { value: "runs", label: <><Rows3 className="w-3 h-3" strokeWidth={1.75} />Runs</> },
  { value: "sessions", label: <><MessagesSquare className="w-3 h-3" strokeWidth={1.75} />Sessions</> },
  {
    value: "errors",
    label: (
      <>
        <CircleAlert className="w-3 h-3" strokeWidth={1.75} />Errors
        {errorCount > 0 && (
          <span className="min-w-[15px] h-[15px] px-[3px] rounded-full bg-[var(--danger)] text-white text-[9.5px] font-semibold flex items-center justify-center tabular-nums">
            {errorCount}
          </span>
        )}
      </>
    ),
  },
];

/* Picked runs wait here for a second one; with two, Compare opens them side by side. It sits
   under the list, rides the bottom of the screen while a long list scrolls, and slides away
   when nothing is picked. */
function CompareBar({ count, picking, onCompare, onClear }: { count: number; picking: boolean; onCompare: () => void; onClear: () => void }) {
  const shown = picking || count > 0;
  return (
    <div className="sticky bottom-4 z-10 mt-3 flex justify-center pointer-events-none">
      <div
        inert={!shown}
        className={`flex items-center gap-2 h-12 pl-4 pr-2 rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-menu text-[13px] text-[var(--text-secondary)] whitespace-nowrap transition-[opacity,transform] [transition-duration:300ms] [transition-timing-function:var(--easing)] ${
          shown ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2"
        }`}
      >
        <span className="pr-1" aria-live="polite">
          {count === 0 ? "Pick two runs to compare" : count === 1 ? "1 run picked · pick one more" : `${count} runs picked`}
        </span>
        <BrandButton disabled={count < 2} onClick={onCompare} className="disabled:opacity-40 disabled:pointer-events-none">
          <GitCompareArrows className="w-3.5 h-3.5" strokeWidth={1.75} />
          Compare
        </BrandButton>
        <CloseButton onClick={onClear} aria-label="Stop comparing" title="Stop comparing" />
      </div>
    </div>
  );
}

function RunList({ agent, agentRuns, loading, onOpen }: { agent: Agent; agentRuns: Run[]; loading: boolean; onOpen: OpenRun }) {
  const [mode, setMode] = useState<Mode>("runs");
  const [filters, setFilters] = useState<RunFilterState>(NO_FILTERS);
  const [query, setQuery] = useState("");
  const [newestFirst, setNewestFirst] = useState(true);
  const [picked, setPicked] = useState<string[]>([]);
  // every row's box is out: the menu's Compare two runs, the only way in on a touch screen
  const [choosing, setChoosing] = useState(false);
  const [comparing, setComparing] = useState(false);

  /* A recorded run's steps are read only where they are shown: a failed run's
     for the errors, and the two picked for a comparison. A tool that failed in
     a run that still succeeded is seen by opening that run. */
  const failed = agentRuns.filter((r) => r.recorded && r.status === "error").map((r) => r.id);
  const treeOf = useSpanTrees(agentRuns, agent, [...failed, ...picked]);
  const errorCount = useMemo(() => errorObservations(agentRuns, treeOf).length, [agentRuns, treeOf]);

  const q = query.trim().toLowerCase();
  let filtered = agentRuns.filter(
    (r) => passesFilters(r, filters) && (q === "" || r.title.toLowerCase().includes(q) || traceId(r).includes(q)),
  );
  if (!newestFirst) filtered = [...filtered].reverse();

  // grouped by the day it started ("Today", "Yesterday"…) or the channel it came in through
  const keyOf = filters.group === "day" ? (r: Run) => r.when : channelOf;
  const groups = filters.group === "none"
    ? [{ key: "all", label: null as string | null, runs: filtered }]
    : [...new Set(filtered.map(keyOf))].map((k) => ({ key: k, label: k, runs: filtered.filter((r) => keyOf(r) === k) }));

  const picking: Picking = {
    picked,
    choosing,
    onPick: (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p.slice(-1), id])),
  };
  // Every picked id came from `agentRuns`, so the lookup always hits.
  const compareRuns = picked.map((id) => agentRuns.find((r) => r.id === id)).filter(Boolean) as Run[];
  const stopPicking = () => { setPicked([]); setChoosing(false); };
  const closeCompare = () => { setComparing(false); stopPicking(); };

  return (
    <AgentPage
      agent={agent}
      title={`${agent.name} traces`}
      subtitle={
        agentRuns.length === 0
          ? "No runs yet"
          : `${agentRuns.length} run${agentRuns.length === 1 ? "" : "s"} recorded`
      }
      description={
        <>
          Every chat and task run — down to each model call and tool
          invocation. Open a trace to see exactly what {agent.name} did, step by step.
        </>
      }
    >
      <>
        {loading ? (
          <div className="py-14 text-center text-[13px] text-[var(--text-tertiary)] tray-box">
            Loading traces…
          </div>
        ) : agentRuns.length === 0 ? (
          <div className="py-14 text-center tray-box">
            <Waypoints className="w-5 h-5 mx-auto mb-2 text-[var(--text-quaternary)]" strokeWidth={1.5} />
            <div className="text-[14px] font-medium text-[var(--text-primary)]">No traces yet</div>
            <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1">
              Start a chat or run a task — every run lands here with its full trace.
            </div>
          </div>
        ) : comparing && compareRuns.length === 2 ? (
          <CompareView runs={compareRuns} treeOf={treeOf} onClose={closeCompare} onOpen={onOpen} />
        ) : (
          <>
            <StatsRow agentRuns={agentRuns} />
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <Segmented role="tablist" label="Show" options={modes(errorCount)} value={mode} onChange={setMode} />
              {mode === "runs" && (
                <>
                  <span className="hidden sm:block flex-1" />
                  <SearchInput
                    raised
                    value={query}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                    placeholder="Search runs by name or ID"
                    aria-label="Search runs"
                    className="flex-1 min-w-[160px] sm:flex-none sm:w-[240px] [&_input]:text-[16px] sm:[&_input]:text-[13px]"
                  />
                  <RunFilterMenu
                    runs={agentRuns}
                    filters={filters}
                    onChange={setFilters}
                    newestFirst={newestFirst}
                    onSort={setNewestFirst}
                    onCompare={() => setChoosing(true)}
                  />
                </>
              )}
            </div>
            {mode === "sessions" ? (
              <SessionsView agentRuns={agentRuns} onOpen={onOpen} />
            ) : mode === "errors" ? (
              <ErrorsView agent={agent} agentRuns={agentRuns} treeOf={treeOf} onOpen={onOpen} />
            ) : (
              <>
                <ActiveFilters filters={filters} onChange={setFilters} />
                {filtered.length === 0 ? (
                  <div className="py-12 text-center text-[13px] text-[var(--text-tertiary)] tray-box">
                    No runs match.{" "}
                    <button
                      type="button"
                      onClick={() => { setFilters(NO_FILTERS); setQuery(""); }}
                      className="press font-medium text-[var(--text-secondary)] underline underline-offset-2 decoration-[var(--hairline-focus)] hover:text-[var(--text-primary)]"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  groups.map((g) => (
                    <div key={g.key} className={g.label ? "mb-6" : ""}>
                      {g.label && <div className="section-label mb-2">{g.label} · {g.runs.length}</div>}
                      <RunTable runs={g.runs} onOpen={onOpen} picking={picking} sort={{ newestFirst, onFlip: () => setNewestFirst((v) => !v) }} />
                    </div>
                  ))
                )}
                <CompareBar count={picked.length} picking={choosing} onCompare={() => setComparing(true)} onClear={stopPicking} />
              </>
            )}
          </>
        )}
      </>
    </AgentPage>
  );
}

export default function TracesPage({ agent, runId = null, onRunChange }: {
  agent: Agent | null;
  /** The run the address names (`/agents/:id/traces/:runId`); the page follows it. */
  runId?: string | null;
  /** A run opened or closed here, for the shell to put in the address: stepping to the next or
      previous run replaces the entry, so Back returns to the list. */
  onRunChange?: (runId: string | null, how?: NavigationOptions) => void;
}) {
  const [open, setOpen] = useState<{ runId: string | null; spanId: string | null }>({ runId, spanId: null });
  // The address moved (Back, a link): the open run follows it, keeping a span the page opened.
  const [lastAddressed, setLastAddressed] = useState(runId);
  if (runId !== lastAddressed) {
    setLastAddressed(runId);
    if (runId !== open.runId) setOpen({ runId, spanId: null });
  }
  const show = (next: { runId: string | null; spanId: string | null }, how?: NavigationOptions) => {
    setOpen(next);
    if (next.runId !== open.runId) onRunChange?.(next.runId, how);
  };
  const recorded = useRecordedRuns(agent?.id ?? null);
  if (!agent) return null;

  const agentRuns = recorded.runs ?? [];
  const openIndex = agentRuns.findIndex((r) => r.id === open.runId);
  const openRun = openIndex >= 0 ? agentRuns[openIndex] : null;
  const openAt = (i: number) => show({ runId: agentRuns[i].id, spanId: null }, { replace: true });

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0">
      {openRun ? (
        <RecordedTrace
          key={`${openRun.id}:${open.spanId || ""}`}
          run={openRun}
          agent={agent}
          initialSpanId={open.spanId}
          onBack={() => show({ runId: null, spanId: null })}
          onPrev={openIndex > 0 ? () => openAt(openIndex - 1) : null}
          onNext={openIndex < agentRuns.length - 1 ? () => openAt(openIndex + 1) : null}
        />
      ) : (
        <RunList
          agent={agent}
          agentRuns={agentRuns}
          loading={recorded.loading}
          onOpen={(runId, spanId = null) => show({ runId, spanId })}
        />
      )}
    </div>
  );
}
